<script lang="ts">
  import { onMount } from 'svelte';
  import Button from '$lib/components/common/button/Button.svelte';
  import { showToast } from '$lib/helpers/toastrHelper';
  import { fetchWithAuth } from '$lib/utils/http/httpUtil';
  import { Info, Warning, Eye, EyeSlash } from 'phosphor-svelte';
  import RequiredBadge from '$lib/components/common/badges/RequiredBadge.svelte';
  
  export let clientId: string = '';
  export let entityId: string = '';
  export let clientName: string = '';
  
  // Entity selection
  type Entity = {
    id: number;
    name: string;
    code: string;
    hasPCSchoolConfig: boolean;
  };
  
  let entities: Entity[] = [];
  let selectedEntity: Entity | null = null;
  let newEntityName: string = '';
  let isLoadingEntities = false;
  let showConfigSection = false;
  
  // PCSchool setup parameters
  let baseUrl = 'https://fsm-api.factsmgt.com.au';
  let subscriptionKey = '';
  let apiKey = '';
  let glCodeFilter = '';
  let userFilter = '';
  let glCodeEntity = '';
  
  // Form state management
  let isLoading = false;
  let isExecuting = false;
  let hasExistingConfig = false;
  
  // Single function to update form data from server response
  function updateFormFromConfig(config: any) {
    baseUrl = config.baseUrl || 'https://fsm-api.factsmgt.com.au';
    subscriptionKey = config.subscriptionKey || '';
    apiKey = config.apiKey || '';
    glCodeFilter = config.glCodeFilter || '';
    userFilter = config.userFilter || '';
    glCodeEntity = config.glCodeEntity || '';
  }
  
  // Token visibility toggles
  let showSubscriptionKey = false;
  let showApiKey = false;
  
  // URL validation
  let urlError = '';
  
  // Common fetch error handler
  function handleApiError(error: any, message: string) {
    console.error(`${message}:`, error);
    showToast({
      title: 'Error',
      message: error?.message || message,
      type: 'error',
    });
  }
  
  function validateBaseUrl(url: string): boolean {
    // Reset error if empty
    if (!url) {
      urlError = '';
      return false;
    }
    
    // Check for HTTPS protocol
    const isHttps = url.startsWith('https://');
    if (!isHttps) {
      urlError = 'URL must start with https://';
      return false;
    }
    
    // URL is valid
    urlError = '';
    return true;
  }
  
  $: isUrlValid = validateBaseUrl(baseUrl);
  $: formIsValid = !!(clientId && baseUrl && isUrlValid && subscriptionKey && apiKey);
  $: canSave = formIsValid && !isExecuting;
  $: showConfigSection = !!(selectedEntity || newEntityName);

  onMount(async () => {
    if (clientId) {
      await loadInitialEntities();
    }
  });
  
  async function loadInitialEntities() {
    if (!clientId || isLoadingEntities) return;
    
    isLoadingEntities = true;
    
    try {
      const response = await fetchWithAuth(`/api/integration/pcschool-entities/${clientId}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.entities) {
          entities = result.entities;
          
          if (entityId) {
            const existingEntity = entities.find(e => e.id.toString() === entityId);
            if (existingEntity) {
              newEntityName = ''; 
              selectedEntity = existingEntity;
              await loadExistingPCSchoolConfig();
            } else {
              entityId = '';
            }
          }
        } else {
          showToast({
            title: 'Error',
            message: result.error || 'Failed to load entities',
            type: 'error',
          });
        }
      }
    } catch (error) {
      handleApiError(error, 'Failed to load entities');
    } finally {
      isLoadingEntities = false;
    }
  }

  async function loadExistingPCSchoolConfig() {
    if (!clientId || !entityId || isLoading) return;

    isLoading = true;
    hasExistingConfig = false;

    try {
      const response = await fetchWithAuth(`/api/integration/pcschool-config/${clientId}/${entityId || 0}`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data.exists) {
          hasExistingConfig = true;
          
          updateFormFromConfig(result.data.config);

          showToast({
            title: 'Existing Configuration Loaded',
            message: 'This client has existing PCSchool configuration.',
            type: 'info',
          });
        } else {
          hasExistingConfig = false;
        }
      }
    } catch (error) {
      console.error('Failed to check existing PCSchool config:', error);
      hasExistingConfig = false;
    } finally {
      isLoading = false;
    }
  }

  function createRequestBody() {
    return {
      clientId: parseInt(clientId),
      entityId: entityId ? parseInt(entityId) : 0,
      baseUrl,
      subscriptionKey,
      apiKey,
      entityName: newEntityName || selectedEntity?.name || clientName,
      glCodeFilter: glCodeFilter || null,
      userFilter: userFilter || null,
      glCodeEntity: glCodeEntity || null
    };
  }
  
  async function savePCSchoolConfiguration() {
    if (!canSave) return;
    
    isExecuting = true;

    try {
      const requestBody = createRequestBody();

      const response = await fetchWithAuth('/api/integration/pcschool-setup', {
        method: 'POST',
        body: JSON.stringify(requestBody),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        const newEntityId = result.data.entityId;

        showToast({
          title: 'Success',
          message: 'PCSchool configuration saved successfully',
          type: 'success',
        });
        
        if (!entityId && newEntityId) {
          entityId = newEntityId.toString();
          hasExistingConfig = true;        
          
          await loadEntitiesWithoutResetting();
          
          const createdEntity = entities.find(e => e.id === newEntityId);
          if (createdEntity) {
            newEntityName = '';
            selectedEntity = createdEntity;
          }
        } else {
          // For existing entities, ensure the selectedEntity is properly set
          await loadEntitiesWithoutResetting();
          
          if (entityId && !selectedEntity) {
            const currentEntity = entities.find(e => e.id.toString() === entityId);
            if (currentEntity) {
              selectedEntity = currentEntity;
            }
          }
        }
      } else {
        let errorMessage = 'Failed to save PCSchool configuration';
        if (Array.isArray(result.errors)) {
          errorMessage = result.errors.join(', ');
        } else if (typeof result.errors === 'string') {
          errorMessage = result.errors;
        } else if (result.errors) {
          errorMessage = JSON.stringify(result.errors);
        }
        
        showToast({
          title: 'Error',
          message: errorMessage,
          type: 'error',
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save PCSchool configuration';
      
      handleApiError(error, 'Failed to save PCSchool configuration');
    } finally {
      isExecuting = false;
    }
  }

  function resetForm() {
    baseUrl = 'https://fsm-api.factsmgt.com.au';
    subscriptionKey = '';
    apiKey = '';
    glCodeFilter = '';
    userFilter = '';
    glCodeEntity = '';
    hasExistingConfig = false;
  }
  
  function resetAll() {
    resetForm();
    selectedEntity = null;
    newEntityName = '';
    entityId = '';
    showConfigSection = false;
  }

  async function loadEntitiesWithoutResetting() {
    if (!clientId || isLoadingEntities) return;
    
    isLoadingEntities = true;
    
    try {
      const response = await fetchWithAuth(`/api/integration/pcschool-entities/${clientId}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.entities) {
          // Store current entity selection to restore it after refresh
          const currentEntityId = selectedEntity?.id;
          
          // Update entities list
          entities = result.entities;
          
          // Restore entity selection if it still exists in the refreshed list
          if (currentEntityId) {
            const updatedEntity = entities.find(e => e.id === currentEntityId);
            if (updatedEntity) {
              selectedEntity = updatedEntity;
            }
          }
        } else {
          console.error('Failed to load entities:', result.error);
        }
      }
    } catch (error) {
      console.error('Failed to reload entities:', error);
    } finally {
      isLoadingEntities = false;
    }
  }
  
  function handleEntitySelection() {
    if (selectedEntity) {
      newEntityName = '';
      entityId = selectedEntity.id.toString();
      resetForm();
      
      if (selectedEntity.hasPCSchoolConfig) {
        loadExistingPCSchoolConfig();
      }
    }
  }
  
  function handleNewEntityInput() {
    if (newEntityName) {
      selectedEntity = null;
      entityId = '';
      resetForm();
    }
  }

  async function loadEntities() {
    await loadInitialEntities();
  }
</script>

<div class="p-5">
  <h3 class="mb-4 text-xl font-semibold">PCSchool Integration Setup</h3>
  <p class="mb-4 text-sm text-surface-700">Configure PCSchool integration for this client with API credentials and sync settings.</p>
  
<div class="space-y-6">
  <!-- CARD 1: Entity Selection Card -->
  <div class="rounded-lg border border-gray-200 bg-gray-50 p-4">
    <h4 class="mb-4 font-semibold">Select or Create Entity</h4>
    <p class="mb-4 text-sm text-gray-600">Choose an existing entity or create a new one for PCSchool integration.</p>
    
    <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
      <!-- Left side: Existing Entity Selection -->
      <div>
        <label for="entity-select" class="mb-2 block text-sm font-medium text-gray-700">
          Existing Entity
        </label>
        <div class="flex items-end gap-3">
          <select
            id="entity-select"
            bind:value={selectedEntity}
            on:change={handleEntitySelection}
            class="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
            disabled={isLoadingEntities || newEntityName !== '' || isExecuting}
          >
            <option value={null}>-- Select Entity --</option>
            {#each entities as entity}
              <option value={entity}>{entity.name} {entity.hasPCSchoolConfig ? '(has PCSchool config)' : ''}</option>
            {/each}
          </select>
          <Button
            label={isLoadingEntities ? "Loading..." : "Refresh"}
            onClick={loadEntities}
            disabled={isLoadingEntities || isExecuting}
            colour="secondary"
            size="small"
          />
        </div>
        {#if isLoadingEntities}
          <p class="mt-2 text-sm text-gray-500">Loading entities...</p>
        {/if}
      </div>
      
      <!-- Right side: New Entity Creation -->
      <div>
        <label for="new-entity-name" class="mb-2 block text-sm font-medium text-gray-700">
          New Entity Name
        </label>
        <input
          id="new-entity-name"
          type="text"
          bind:value={newEntityName}
          on:input={handleNewEntityInput}
          placeholder="Enter new entity name"
          class="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
          disabled={selectedEntity !== null || isExecuting}
        />
        <p class="mt-2 text-xs text-gray-500">Leave blank to select an existing entity</p>
      </div>
    </div>
  </div>

  <!-- CARD 2: PCSchool Configuration Card (only shown when entity is selected or new entity name provided) -->
  {#if showConfigSection}
    <div class="space-y-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
      <h4 class="font-semibold">PCSchool Configuration</h4>

      {#if hasExistingConfig}
        <div class="mb-4 rounded-lg bg-blue-50 p-4">
          <div class="flex items-center gap-2">
            <Info size={16} class="text-blue-600" />
            <span class="text-sm font-medium text-blue-800">Existing Configuration</span>
          </div>
          <p class="mt-1 text-sm text-blue-700">This entity already has PCSchool configuration. You can update the settings below.</p>
        </div>
      {:else if selectedEntity}
        <div class="mb-4 rounded-lg bg-green-50 p-4">
          <p class="text-sm text-green-700">Selected entity: <strong>{selectedEntity.name}</strong></p>
        </div>
      {:else if newEntityName}
        <div class="mb-4 rounded-lg bg-yellow-50 p-4">
          <p class="text-sm text-yellow-700">Creating new entity: <strong>{newEntityName}</strong></p>
        </div>
      {/if}

      <div class="grid grid-cols-1 gap-6">
        <!-- API Base URL -->
        <div>
          <label for="api-base-url" class="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
            API Base URL <RequiredBadge />
          </label>
          <input
            id="api-base-url"
            type="url"
            bind:value={baseUrl}
            class="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 {urlError ? 'border-red-500' : ''}"
            placeholder="https://example.pcschool.com.au"
          />
          {#if urlError}
            <p class="mt-1 text-sm text-red-600">{urlError}</p>
          {/if}
          <p class="mt-1 text-xs text-gray-500">The base URL for your PCSchool API (must use HTTPS)</p>
        </div>

        <!-- Subscription Key -->
        <div>
          <label for="subscription-key" class="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
            Subscription Key <RequiredBadge />
          </label>
          <div class="relative">
            {#if showSubscriptionKey}
              <input
                id="subscription-key"
                type="text"
                bind:value={subscriptionKey}
                class="w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Enter subscription key"
              />
            {:else}
              <input
                id="subscription-key"
                type="password"
                bind:value={subscriptionKey}
                class="w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Enter subscription key"
              />
            {/if}
            <button
              type="button"
              class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              on:click={() => showSubscriptionKey = !showSubscriptionKey}
            >
              {#if showSubscriptionKey}
                <EyeSlash size={16} />
              {:else}
                <Eye size={16} />
              {/if}
            </button>
          </div>
        </div>

        <!-- API Key -->
        <div>
          <label for="api-key" class="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700">
            API Key <RequiredBadge />
          </label>
          <div class="relative">
            {#if showApiKey}
              <input
                id="api-key"
                type="text"
                bind:value={apiKey}
                class="w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Enter API key"
              />
            {:else}
              <input
                id="api-key"
                type="password"
                bind:value={apiKey}
                class="w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Enter API key"
              />
            {/if}
            <button
              type="button"
              class="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              on:click={() => showApiKey = !showApiKey}
            >
              {#if showApiKey}
                <EyeSlash size={16} />
              {:else}
                <Eye size={16} />
              {/if}
            </button>
          </div>
        </div>

        <!-- Optional Filters -->
        <div class="border-t pt-6">
          <h4 class="mb-4 text-sm font-medium text-gray-700">Optional Filters</h4>
          
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <!-- GL Code Entity -->
            <div>
              <label for="gl-code-entity" class="mb-2 block text-sm font-medium text-gray-700">GL Code Entity</label>
              <input
                id="gl-code-entity"
                type="text"
                bind:value={glCodeEntity}
                class="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="e.g., COLLEGE"
              />
              <p class="mt-1 text-xs text-gray-500">Entity for GL code integration (default: COLLEGE)</p>
            </div>

            <!-- GL Code Filter -->
            <div>
              <label for="gl-code-filter" class="mb-2 block text-sm font-medium text-gray-700">GL Code Filter</label>
              <input
                id="gl-code-filter"
                type="text"
                bind:value={glCodeFilter}
                class="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="e.g., D"
              />
              <p class="mt-1 text-xs text-gray-500">Filter GL codes by status (optional)</p>
            </div>

            <!-- User Filter -->
            <div>
              <label for="user-filter" class="mb-2 block text-sm font-medium text-gray-700">User Filter</label>
              <input
                id="user-filter"
                type="text"
                bind:value={userFilter}
                class="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="e.g., V,T"
              />
              <p class="mt-1 text-xs text-gray-500">Filter users by status (comma-separated, optional)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  {/if}
    
  <div class="mt-6 flex justify-end gap-3">
    <Button label="Reset All" onClick={resetAll} disabled={isExecuting} colour="secondary" />
    {#if showConfigSection}
      <Button 
        label={isExecuting ? 'Saving...' : hasExistingConfig ? 'Update Configuration' : 'Save Configuration'} 
        onClick={savePCSchoolConfiguration} 
        disabled={!canSave} 
        colour="primary" 
      />
    {/if}
  </div>
</div>
</div>
